import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import './index.css';

// ── Public Pages ──────────────────────────────────────────────────────────────
import Login from './pages/Login';
import Register from './pages/Register';
import Store from './pages/Store';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Zones from './pages/Zones';
import ZoneDetail from './pages/ZoneDetail';
import Checkout from './pages/Checkout';
import PaymentStatus from './pages/PaymentStatus';
import ProductDetail from './pages/ProductDetail';

// ── Service Forms ──────────────────────────────────────────────────────────────
import DesktopServiceRequestForm  from './components/Forms/DesktopServiceRequestForm';
import LaptopServiceRequestForm   from './components/Forms/LaptopServiceRequestForm';
import CCTVServiceRequestForm     from './components/Forms/CCTVServiceRequestForm';
import PrinterServiceRequestForm  from './components/Forms/PrinterServiceRequestForm';
import ElectricalServiceRequestForm from './components/Forms/ElectricalServiceRequestForm';
import PlumbingServiceRequestForm from './components/Forms/PlumbingServiceRequestForm';

// ── My Account ────────────────────────────────────────────────────────────────
import MyOrders      from './pages/MyOrders';
import MyAddresses   from './pages/MyAddresses';
import ChangePassword from './pages/ChangePassword';
import EditProfile   from './pages/EditProfile';

// ── Admin Shell ───────────────────────────────────────────────────────────────
import AdminSidebar  from './components/AdminSidebar'; // now renders Outlet inside
import Dashboard     from './pages/admin/Dashboard';
import Products      from './pages/admin/Products';
import Coupons       from './pages/admin/Coupons';
import Users         from './pages/admin/Users';
import Vendors       from './pages/admin/Vendors';
import Orders        from './pages/admin/Orders';
import Reviews       from './pages/admin/Reviews';
import ServiceRequests  from './pages/admin/ServiceRequests';
import ServiceReminders from './pages/admin/ServiceReminders';
import CustomerPhonebook from './pages/admin/CustomerPhonebook';
import AdminZones    from './pages/admin/Zones';
import Expenses      from './pages/admin/Expenses';
import JobMonitor    from './pages/admin/JobMonitor';

// ── Billing (NEW) ─────────────────────────────────────────────────────────────
import InvoiceRegister from './pages/admin/billing/InvoiceRegister';
import InvoiceForm     from './pages/admin/billing/InvoiceForm';
import InvoiceDetail   from './pages/admin/billing/InvoiceDetail';
import Parties         from './pages/admin/billing/Parties';
import { OutstandingReceivables, OutstandingPayables } from './pages/admin/billing/Outstanding';

// ── GST (NEW) ─────────────────────────────────────────────────────────────────
import GstDashboard from './pages/admin/gst/GstDashboard';

// ── Route Guards ──────────────────────────────────────────────────────────────
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdminOrVendor, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50"><div className="text-xl font-bold text-purple-600">Loading…</div></div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (adminOnly && !isAdminOrVendor) return <Navigate to="/store" />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50"><div className="text-xl font-bold text-purple-600">Loading…</div></div>;
  if (isAuthenticated) return <Navigate to={isAdmin ? '/admin' : '/store'} />;
  return children;
};

// ── Placeholder for unbuilt GST subpages ─────────────────────────────────────
const ComingSoon = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-64 gap-4">
    <div className="text-5xl">🚧</div>
    <h2 className="text-xl font-black text-slate-900">{title}</h2>
    <p className="text-slate-500 text-sm font-medium">This module will be available in the next release.</p>
  </div>
);

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-right" toastOptions={{
            duration: 3000,
            style: { background: '#1e293b', color: '#f1f5f9', borderRadius: '12px', fontSize: '13px', fontWeight: 600 },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error:   { duration: 4000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }} />

          <Routes>
            {/* ── Public ──────────────────────────────────────────────── */}
            <Route path="/login"    element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/store"    element={<Store />} />
            <Route path="/cart"     element={<ProtectedRoute><Cart /></ProtectedRoute>} />
            <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
            <Route path="/zones"       element={<Zones />} />
            <Route path="/zones/:id"   element={<ZoneDetail />} />
            <Route path="/checkout"    element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/payment-status" element={<PaymentStatus />} />
            <Route path="/product/:id" element={<ProductDetail />} />

            {/* ── My Account ──────────────────────────────────────────── */}
            <Route path="/my-orders"      element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="/my-addresses"   element={<ProtectedRoute><MyAddresses /></ProtectedRoute>} />
            <Route path="/change-password"element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/edit-profile"   element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />

            {/* ── Service Forms ────────────────────────────────────────── */}
            <Route path="Desktop-service"   element={<DesktopServiceRequestForm />} />
            <Route path="Laptop-service"    element={<LaptopServiceRequestForm />} />
            <Route path="CCTV-service"      element={<CCTVServiceRequestForm />} />
            <Route path="Printer-service"   element={<PrinterServiceRequestForm />} />
            <Route path="Plumbing-service"  element={<PlumbingServiceRequestForm />} />
            <Route path="Electrical-service"element={<ElectricalServiceRequestForm />} />

            {/* ── Admin (protected) ────────────────────────────────────── */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminSidebar /></ProtectedRoute>}>

              {/* Core */}
              <Route index element={<Dashboard />} />
              <Route path="products"          element={<Products />} />
              <Route path="coupons"           element={<Coupons />} />
              <Route path="users"             element={<Users />} />
              <Route path="vendors"           element={<Vendors />} />
              <Route path="orders"            element={<Orders />} />
              <Route path="reviews"           element={<Reviews />} />
              <Route path="service-requests"  element={<ServiceRequests />} />
              <Route path="service-reminders" element={<ServiceReminders />} />
              <Route path="phonebook"         element={<CustomerPhonebook />} />
              <Route path="zones"             element={<AdminZones />} />
              <Route path="expenses"          element={<Expenses />} />
              <Route path="job-monitor"       element={<JobMonitor />} />

              {/* ── Billing ─────────────────────────────────────────── */}
              <Route path="billing"           element={<InvoiceRegister />} />
              <Route path="billing/new/:type" element={<InvoiceForm />} />
              <Route path="billing/new"       element={<InvoiceForm />} />
              <Route path="billing/:id"       element={<InvoiceDetail />} />
              <Route path="billing/:id/edit"  element={<InvoiceForm />} />

              {/* ── Accounts ────────────────────────────────────────── */}
              <Route path="parties"     element={<Parties />} />
              <Route path="receivables" element={<OutstandingReceivables />} />
              <Route path="payables"    element={<OutstandingPayables />} />

              {/* ── GST ──────────────────────────────────────────────── */}
              <Route path="gst"                  element={<GstDashboard />} />
              <Route path="gst/gstr1"            element={<ComingSoon title="GSTR-1 Return" />} />
              <Route path="gst/gstr3b"           element={<ComingSoon title="GSTR-3B Return" />} />
              <Route path="gst/gstr2ab"          element={<ComingSoon title="GSTR-2A / 2B" />} />
              <Route path="gst/reconciliation"   element={<ComingSoon title="GST Reconciliation" />} />
              <Route path="gst/itc"              element={<ComingSoon title="ITC Management" />} />
              <Route path="gst/ledgers"          element={<ComingSoon title="GST Ledgers" />} />
              <Route path="gst/reports"          element={<ComingSoon title="GST Reports" />} />
              <Route path="gst/filing-history"   element={<ComingSoon title="Filing History" />} />
              <Route path="gst/settings"         element={<ComingSoon title="GST Settings" />} />
            </Route>

            {/* ── Root & 404 ───────────────────────────────────────────── */}
            <Route path="/" element={<Navigate to="/store" />} />
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
                  <p className="text-xl text-gray-600 mb-6">Page not found</p>
                  <a href="/store" className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-bold">
                    Go Home
                  </a>
                </div>
              </div>
            } />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
